import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const H_LOGO_URL =
  "/brand/hireloop-h.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="login-page"
      className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md flex-col justify-center px-6 py-16"
    >
      <div className="flex flex-col items-center text-center">
        <img
          src={H_LOGO_URL}
          alt="Hireloop PRO"
          data-testid="login-logo"
          className="h-16 w-16 object-contain"
          style={{ mixBlendMode: "multiply" }}
        />
        <div className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
          Welcome back
        </div>
        <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
          Sign in to Hireloop PRO
        </h1>
      </div>
      <form onSubmit={onSubmit} className="mt-10 space-y-5">
        <div>
          <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.2em] text-black">
            Email
          </Label>
          <Input
            id="email"
            data-testid="login-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 rounded-sm border-slate-300"
          />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.2em] text-black">
              Password
            </Label>
            <Link
              to="/forgot-password"
              data-testid="login-forgot-link"
              className="text-xs font-medium text-blue-700 hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          <Input
            id="password"
            data-testid="login-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 rounded-sm border-slate-300"
          />
        </div>
        <Button
          type="submit"
          data-testid="login-submit-btn"
          disabled={loading}
          className="w-full rounded-sm bg-blue-700 py-6 hover:bg-blue-600"
        >
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Sign in
        </Button>
      </form>
      <p className="mt-8 text-center text-sm text-slate-500">
        New here?{" "}
        <Link
          to="/register"
          data-testid="login-to-register-link"
          className="font-medium text-blue-700 hover:underline"
        >
          Create an account
        </Link>
      </p>
    </main>
  );
}
